import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { DiretoriaSparkline } from '@/components/Diretoria/DiretoriaSparkline';
import {
  DiretoriaSqlButton,
  type DiretoriaSqlButtonProps,
} from '@/components/Diretoria/DiretoriaSqlButton';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import type { DiretoriaInadimplenciaPayload } from '@/lib/diretoriaErpIndicatorBuilders';
import type { DiretoriaDashboardStats } from '@/lib/diretoriaDashboard';

const COLOR_A = 'hsl(222 47% 18%)';
const COLOR_B = '#ca8a04';
const COLOR_C = '#0f766e';
const PRODUCT_COLORS = [COLOR_A, COLOR_B, COLOR_C, '#7c3aed', '#b45309', '#0369a1'] as const;

function pctOf(part: number, total: number): string {
  if (!total || !Number.isFinite(total)) return '—';
  return `${((part / total) * 100).toFixed(1).replace('.', ',')}%`;
}

function truncateLabel(raw: unknown, max = 16): string {
  const s = String(raw ?? '').trim() || '—';
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Tooltip Recharts que não quebra se `payload` vier undefined (crash reading '0'). */
function SafeChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number | string; color?: string }>;
  label?: string | number;
}) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs shadow-md">
      {label != null && label !== '' ? (
        <p className="mb-0.5 font-medium text-foreground">{String(label)}</p>
      ) : null}
      {payload.map((entry, i) => (
        <p key={`${entry?.name ?? i}`} className="tabular-nums text-muted-foreground">
          <span style={{ color: entry?.color }}>{entry?.name ?? 'Série'}</span>:{' '}
          {entry?.value == null || entry.value === ''
            ? '—'
            : typeof entry.value === 'number'
              ? entry.value.toLocaleString('pt-BR')
              : String(entry.value)}
        </p>
      ))}
    </div>
  );
}

class ChartBlockBoundary extends React.Component<
  { children: React.ReactNode; fallback?: string },
  { error?: Error }
> {
  state: { error?: Error } = {};
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error('Diretoria Faturamento chart crash:', error);
  }
  render() {
    if (this.state.error) {
      return (
        <p className="flex h-full min-h-[8rem] items-center justify-center px-3 text-center text-sm text-muted-foreground">
          {this.props.fallback ?? 'Gráfico indisponível com os dados atuais'}
        </p>
      );
    }
    return this.props.children;
  }
}

export interface FaturamentoProdutoPayload {
  itens: Array<{
    name: string;
    produto: string;
    nomeEmpresa: string;
    totalPago: number;
  }>;
  produtos: string[];
  dimensaoProduto?: string;
  querySql?: string;
}

export interface FrotaIdadePayload {
  buckets: Array<{
    marca: string;
    modelo: string;
    anoFabricacao: string;
    anoModelo: string;
    quantidade: number;
  }>;
  totalMotos: number;
  querySql?: string;
}

export interface RentabilidadeRankingPayload {
  ranking: Array<{
    placa: string;
    marca: string;
    modelo: string;
    receita: number;
    custo: number;
    margem: number;
    margemPct: number | null;
  }>;
  kmDisponivel: boolean;
  querySql?: string;
}

export interface InfracoesExtPayload {
  mensal?: Array<{ name: string; qtdPagas: number; valorPagas: number }>;
  porEmpresa?: Array<{
    nomeEmpresa: string;
    qtdPagas: number;
    qtdVencidas: number;
    qtdAVencer: number;
    valorPagas: number;
    valorAberto: number;
  }>;
}

export interface DiretoriaFaturamentoSectionProps {
  indicators: DiretoriaIndicator[];
  onOpenIndicator: (ind: DiretoriaIndicator) => void;
  resolveIndicatorSql: (ind: DiretoriaIndicator) => DiretoriaSqlButtonProps;
  formatCurrency: (n: number) => string;
  companyId?: string;
  inadimplenciaErp?: DiretoriaInadimplenciaPayload | null;
  dashboardStats?: DiretoriaDashboardStats | null;
  faturamentoProduto?: FaturamentoProdutoPayload | null;
  frotaIdade?: FrotaIdadePayload | null;
  rentabilidadeRanking?: RentabilidadeRankingPayload | null;
  infracoesExt?: InfracoesExtPayload | null;
}

export const DiretoriaFaturamentoSection: React.FC<DiretoriaFaturamentoSectionProps> = ({
  indicators,
  onOpenIndicator,
  resolveIndicatorSql,
  formatCurrency,
  companyId,
  inadimplenciaErp,
  dashboardStats,
  faturamentoProduto,
  frotaIdade,
  rentabilidadeRanking,
  infracoesExt,
}) => {
  const [inadSource, setInadSource] = useState<'erp' | 'cobranca'>('erp');

  const titulosHref = useMemo(() => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    const q = params.toString();
    return q ? `/titulos?${q}` : '/titulos';
  }, [companyId]);

  const kanbanHref = useMemo(() => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    const q = params.toString();
    return q ? `/kanban?${q}` : '/kanban';
  }, [companyId]);

  const cobrancaAging = useMemo(() => {
    if (!dashboardStats) return null;
    const faixas = [
      {
        name: '1–2 dias',
        qtd: Number(dashboardStats.aging1a2Dias?.count ?? 0),
        saldo: Number(dashboardStats.aging1a2Dias?.valor ?? 0),
      },
      {
        name: '3–6 dias',
        qtd: Number(dashboardStats.aging3a6Dias?.count ?? 0),
        saldo: Number(dashboardStats.aging3a6Dias?.valor ?? 0),
      },
      {
        name: '7+ dias',
        qtd: Number(dashboardStats.agingAcima7Dias?.count ?? 0),
        saldo: Number(dashboardStats.agingAcima7Dias?.valor ?? 0),
      },
    ];
    const saldo = Number(dashboardStats.valorTotal ?? faixas.reduce((s, f) => s + f.saldo, 0));
    const titulos = Number(
      dashboardStats.titulosVencidos ?? faixas.reduce((s, f) => s + f.qtd, 0)
    );
    const porEmpresa = (dashboardStats.titulosPorEmpresa ?? [])
      .filter((row) => row && typeof row === 'object')
      .slice()
      .sort((a, b) => Number(b.valor ?? 0) - Number(a.valor ?? 0))
      .slice(0, 8);
    return { faixas, saldo, titulos, porEmpresa };
  }, [dashboardStats]);

  const produtoStacked = useMemo(() => {
    const itens = (faturamentoProduto?.itens ?? []).filter(
      (item) => item && String(item.name ?? '').trim() && String(item.produto ?? '').trim()
    );
    const byMonth = new Map<string, Record<string, number | string>>();
    const topProdutos = [...(faturamentoProduto?.produtos ?? [])]
      .map((p) => String(p ?? '').trim())
      .filter(Boolean)
      .slice(0, 6);
    for (const item of itens) {
      const produto = String(item.produto).trim();
      const name = String(item.name).trim();
      if (!topProdutos.includes(produto) && topProdutos.length >= 6) continue;
      if (!topProdutos.includes(produto)) topProdutos.push(produto);
      const row = byMonth.get(name) ?? { name };
      row[produto] = (Number(row[produto] ?? 0) || 0) + Number(item.totalPago ?? 0);
      byMonth.set(name, row);
    }
    return {
      data: Array.from(byMonth.values()),
      keys: topProdutos.slice(0, 6),
    };
  }, [faturamentoProduto]);

  const frotaBars = useMemo(() => {
    const byAno = new Map<string, number>();
    for (const b of frotaIdade?.buckets ?? []) {
      if (!b) continue;
      const ano = String(b.anoFabricacao || b.anoModelo || 'S/A');
      byAno.set(ano, (byAno.get(ano) ?? 0) + Number(b.quantidade ?? 0));
    }
    return Array.from(byAno.entries())
      .map(([name, quantidade]) => ({ name, quantidade }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .slice(-12);
  }, [frotaIdade]);

  const rankingBars = useMemo(
    () =>
      (rentabilidadeRanking?.ranking ?? [])
        .filter((r) => r)
        .map((r) => ({
          name: String(r.placa || '—'),
          margem: Math.round(Number(r.margem) || 0),
          receita: Math.round(Number(r.receita) || 0),
        })),
    [rentabilidadeRanking]
  );

  const mensalMultas = useMemo(
    () =>
      (infracoesExt?.mensal ?? [])
        .filter((m) => m && String(m.name ?? '').trim())
        .map((m) => ({
          name: String(m.name),
          qtdPagas: Number(m.qtdPagas ?? 0) || 0,
          valorPagas: Number(m.valorPagas ?? 0) || 0,
        })),
    [infracoesExt]
  );

  const erpSaldo = Number(inadimplenciaErp?.saldoVencido ?? 0) || 0;
  const erpTitulos = Number(inadimplenciaErp?.titulosVencidos ?? 0) || 0;
  const erpFaixas = (inadimplenciaErp?.faixas ?? []).filter((f) => f && f.faixa != null);
  const erpTotalFaixa = erpFaixas.reduce((s, f) => s + Number(f.saldo ?? 0), 0) || erpSaldo;

  const safeIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground">Faturamento</h2>
          <p className="text-xs text-muted-foreground">
            Produto × período, inadimplência, multas, custo operacional e frota · fonte principal ERP
            FlyStart (aba Cobrança = banco PortalFly)
          </p>
        </div>
      </div>

      {/* Inadimplência dual */}
      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Inadimplência</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-md border border-border p-0.5 text-xs">
                <button
                  type="button"
                  className={cn(
                    'rounded px-2.5 py-1 font-medium transition-colors',
                    inadSource === 'erp' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                  )}
                  onClick={() => setInadSource('erp')}
                >
                  ERP
                </button>
                <button
                  type="button"
                  className={cn(
                    'rounded px-2.5 py-1 font-medium transition-colors',
                    inadSource === 'cobranca'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground'
                  )}
                  onClick={() => setInadSource('cobranca')}
                >
                  Cobrança
                </button>
              </div>
              <Link
                to={titulosHref}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Títulos
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
              <Link
                to={kanbanHref}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Kanban
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-2">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {inadSource === 'erp' ? (
              <>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">Saldo vencido</p>
                  <p className={cn('font-heading text-xl font-bold tabular-nums', diretoriaToneTextClass('bad'))}>
                    {formatCurrency(erpSaldo)}
                  </p>
                  <p className="text-xs text-muted-foreground">{pctOf(erpSaldo, erpTotalFaixa || erpSaldo)} do aging</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">Títulos</p>
                  <p className="font-heading text-xl font-bold tabular-nums">{erpTitulos}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">Novos no mês</p>
                  <p className="font-heading text-xl font-bold tabular-nums">
                    {inadimplenciaErp?.novosMes ?? 0}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">Saldo vencido</p>
                  <p className={cn('font-heading text-xl font-bold tabular-nums', diretoriaToneTextClass('bad'))}>
                    {formatCurrency(cobrancaAging?.saldo ?? 0)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">Títulos</p>
                  <p className="font-heading text-xl font-bold tabular-nums">{cobrancaAging?.titulos ?? 0}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase text-muted-foreground">Em 7+ dias</p>
                  <p className="font-heading text-xl font-bold tabular-nums">
                    {cobrancaAging?.faixas[2]?.qtd ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {pctOf(cobrancaAging?.faixas[2]?.saldo ?? 0, cobrancaAging?.saldo ?? 0)} do saldo
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="h-48">
            <ChartBlockBoundary>
              {inadSource === 'erp' && erpFaixas.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={erpFaixas.map((f) => ({
                      name: String(f.faixa),
                      qtd: Number(f.qtd ?? 0) || 0,
                      saldo: Math.round(Number(f.saldo ?? 0) || 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Bar dataKey="qtd" name="Qtd." fill={COLOR_A} isAnimationActive={false} />
                    <Bar dataKey="saldo" name="Saldo" fill={COLOR_B} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : cobrancaAging ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={cobrancaAging.faixas.map((f) => ({
                      name: f.name,
                      qtd: f.qtd,
                      saldo: Math.round(f.saldo),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Bar dataKey="qtd" name="Qtd." fill={COLOR_A} isAnimationActive={false} />
                    <Bar dataKey="saldo" name="Saldo" fill={COLOR_B} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem dados de inadimplência
                </p>
              )}
            </ChartBlockBoundary>
          </div>
          {inadSource === 'cobranca' && (cobrancaAging?.porEmpresa?.length ?? 0) > 0 ? (
            <div className="lg:col-span-2">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Por empresa (cobrança)
              </p>
              <div className="overflow-x-auto rounded-md border border-border/80">
                <table className="w-full min-w-[320px] text-left text-sm">
                  <thead className="bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Empresa</th>
                      <th className="px-3 py-2 text-right font-medium">Títulos</th>
                      <th className="px-3 py-2 text-right font-medium">Saldo</th>
                      <th className="px-3 py-2 text-right font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cobrancaAging!.porEmpresa.map((row, idx) => (
                      <tr key={`${row.empresa ?? idx}`} className="border-t border-border/60">
                        <td className="px-3 py-1.5">{row.empresa || '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.count ?? 0}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {formatCurrency(Number(row.valor ?? 0))}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {pctOf(Number(row.valor ?? 0), cobrancaAging?.saldo ?? 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Faturamento por produto</CardTitle>
            <p className="text-xs text-muted-foreground">
              {faturamentoProduto?.dimensaoProduto ??
                'Baixas tipocontaid=1 agrupadas pela descrição da conta · ERP FlyStart'}
            </p>
          </CardHeader>
          <CardContent className="h-64 pt-0">
            <ChartBlockBoundary>
              {produtoStacked.data.length > 0 && produtoStacked.keys.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={produtoStacked.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    {produtoStacked.keys.map((key, i) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={PRODUCT_COLORS[i % PRODUCT_COLORS.length]}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem faturamento por produto no período
                </p>
              )}
            </ChartBlockBoundary>
          </CardContent>
        </Card>

        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Multas pagas (mês a mês)</CardTitle>
          </CardHeader>
          <CardContent className="h-64 pt-0">
            <ChartBlockBoundary>
              {mensalMultas.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mensalMultas}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="qtdPagas"
                      name="Qtd. pagas"
                      stroke={COLOR_A}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="valorPagas"
                      name="Valor"
                      stroke={COLOR_B}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem série de multas no período
                </p>
              )}
            </ChartBlockBoundary>
          </CardContent>
        </Card>

        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Multas por empresa</CardTitle>
          </CardHeader>
          <CardContent className="h-64 pt-0">
            <ChartBlockBoundary>
              {(infracoesExt?.porEmpresa?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(infracoesExt!.porEmpresa ?? []).slice(0, 8).map((e) => ({
                      name: truncateLabel(e?.nomeEmpresa),
                      vencidas: Number(e?.qtdVencidas ?? 0) || 0,
                      aVencer: Number(e?.qtdAVencer ?? 0) || 0,
                      pagas: Number(e?.qtdPagas ?? 0) || 0,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Bar dataKey="pagas" name="Pagas" fill={COLOR_C} stackId="m" isAnimationActive={false} />
                    <Bar dataKey="aVencer" name="A vencer" fill={COLOR_B} stackId="m" isAnimationActive={false} />
                    <Bar dataKey="vencidas" name="Vencidas" fill={COLOR_A} stackId="m" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem breakdown por empresa
                </p>
              )}
            </ChartBlockBoundary>
          </CardContent>
        </Card>

        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Envelhecimento da frota</CardTitle>
            <p className="text-xs text-muted-foreground">
              {frotaIdade?.totalMotos ?? 0} motos ativas · ano de fabricação · ERP FlyStart
            </p>
          </CardHeader>
          <CardContent className="h-64 pt-0">
            <ChartBlockBoundary>
              {frotaBars.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={frotaBars}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Bar dataKey="quantidade" name="Qtd." fill={COLOR_A} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem dados de idade da frota
                </p>
              )}
            </ChartBlockBoundary>
          </CardContent>
        </Card>

        <Card className="border-border/80 xl:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">Top 10 placas — rentabilidade</CardTitle>
              {!rentabilidadeRanking?.kmDisponivel ? (
                <Badge variant="outline" className="text-[10px] font-normal">
                  km em breve
                </Badge>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="h-64 pt-0">
            <ChartBlockBoundary>
              {rankingBars.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rankingBars} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Bar dataKey="margem" name="Margem" fill={COLOR_C} isAnimationActive={false} />
                    <Bar dataKey="receita" name="Receita" fill={COLOR_A} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem ranking de rentabilidade
                </p>
              )}
            </ChartBlockBoundary>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {safeIndicators.map((ind) => {
          const kpis = Array.isArray(ind.kpis) ? ind.kpis : [];
          const pk = Math.min(ind.primaryKpiIndex ?? 0, Math.max(0, kpis.length - 1));
          const primary = kpis[pk];
          const secondary = kpis.filter((_, i) => i !== pk);
          let sqlProps: DiretoriaSqlButtonProps = {};
          try {
            sqlProps = resolveIndicatorSql(ind) ?? {};
          } catch (err) {
            console.error('resolveIndicatorSql failed', ind.id, err);
          }
          return (
            <Card key={ind.id} className="relative h-full border-border/80 hover:shadow-card-hover">
              <div className="absolute right-2 top-2 z-10">
                <DiretoriaSqlButton {...sqlProps} stopPropagation />
              </div>
              <button
                type="button"
                onClick={() => onOpenIndicator(ind)}
                className="w-full text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <CardHeader className="pb-2 pr-10">
                  <CardTitle className="text-base leading-snug">{ind.title}</CardTitle>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{ind.shortDescription}</p>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  {primary ? (
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {primary.label}
                      </p>
                      <p
                        className={cn(
                          'break-words font-heading text-xl font-bold tabular-nums sm:text-2xl',
                          diretoriaToneTextClass(primary.tone)
                        )}
                      >
                        {primary.value}
                      </p>
                    </div>
                  ) : null}
                  <ChartBlockBoundary fallback="Prévia indisponível">
                    <DiretoriaSparkline indicator={ind} />
                  </ChartBlockBoundary>
                  {secondary.map((k, i) => (
                    <div
                      key={`${k?.label ?? i}`}
                      className="flex items-baseline justify-between gap-2 border-t border-border/60 pt-2 first:border-t-0 first:pt-0"
                    >
                      <span className="min-w-0 shrink text-xs text-muted-foreground">
                        {k?.label ?? '—'}
                      </span>
                      <span
                        className={cn(
                          'max-w-[60%] break-words text-right text-sm font-semibold',
                          diretoriaToneTextClass(k?.tone)
                        )}
                      >
                        {k?.value ?? '—'}
                      </span>
                    </div>
                  ))}
                  <span className="inline-block pt-1 text-xs font-medium text-primary">Ver análise</span>
                </CardContent>
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
